import type {
  changeUser,
  destroy,
  isPushBlocked,
  isPushPermissionGranted,
  isPushSupported,
  logCustomEvent,
  openSession,
  requestPushPermission,
  unregisterPush,
  wipeData,
} from '@braze/web-sdk'
import createBraze from '#shared/lib/createBraze'

let braze: {
  openSession: typeof openSession
  changeUser: typeof changeUser
  destroy: typeof destroy
  isPushSupported: typeof isPushSupported
  isPushPermissionGranted: typeof isPushPermissionGranted
  isPushBlocked: typeof isPushBlocked
  requestPushPermission: typeof requestPushPermission
  unregisterPush: typeof unregisterPush
  logCustomEvent: typeof logCustomEvent
  wipeData: typeof wipeData
}

export default async function useBraze() {
  const { isPotentiallyABot } = useBotScore()
  const runtimeConfig = useRuntimeConfig()

  if (isPotentiallyABot) {
    return {
      openSession: () => {},
      setUser: () => {},
      destroy: () => {},
      logCustomEvent: () => false,
      canRequestPushMessagesPermission: () => false,
      isWebPushSupported: () => false,
      isWebPushBlocked: () => false,
      isWebPushAllowed: () => false,
      requestPushMessagesPermission: () => {},
      removePushMessagePermission: () => {},
      reset: () => {},
    }
  }

  if (!braze && isMetaClient) {
    const runtimeConfig = useRuntimeConfig()
    const $brazeConfig = runtimeConfig.public.braze

    braze = await createBraze($brazeConfig.apiKey, $brazeConfig.sdkUrl)
  }

  const canRequestPushMessagesPermission = () => {
    return braze.isPushSupported() && !braze.isPushPermissionGranted() && !braze.isPushBlocked()
  }

  const isWebPushSupported = () => {
    return braze.isPushSupported()
  }

  const isWebPushBlocked = () => {
    return braze.isPushBlocked()
  }

  const isWebPushAllowed = () => {
    return braze.isPushPermissionGranted()
  }

  const requestPushMessagesPermission = (success: () => void, denied: (arg0: boolean) => void) => {
    braze.requestPushPermission(
      () => success(),
      (isTemporary) => denied(isTemporary),
    )
  }

  const reset = () => {
    braze.wipeData()
  }

  const removePushMessagePermission = () => {
    braze.unregisterPush()
  }

  const logCustomEventWithMetadata: typeof logCustomEvent = (eventName, eventProperties) => {
    const metaData = {
      env: runtimeConfig.public.env,
    }

    return braze?.logCustomEvent(eventName, { ...eventProperties, ...metaData })
  }

  return {
    openSession: braze?.openSession,
    setUser: braze?.changeUser,
    destroy: braze?.destroy,
    logCustomEvent: logCustomEventWithMetadata,
    canRequestPushMessagesPermission,
    isWebPushSupported,
    isWebPushBlocked,
    isWebPushAllowed,
    requestPushMessagesPermission,
    removePushMessagePermission,
    reset,
  }
}
